import { IClauseImportStats } from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import { Table } from "@/components/shared/table/Table";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WarningIcon from "@mui/icons-material/Warning";
import {
    Stack,
    SxProps,
    Theme,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useState } from "react";
import { SkippedClausesList } from "./SkippedClausesList";

const buttonSx: SxProps<Theme> = {
    borderRadius: 2,
    py: 1,
};

interface IProps {
    /**
     * The results of the preprocessing step
     */
    results: IClauseImportStats;

    /**
     * Callback to close the dialog
     */
    onClose: () => void;
}

/**
 * PreprocessResult component
 */
export const PreprocessResult = ({ results, onClose }: IProps) => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
    const [showSkippedDetails, setShowSkippedDetails] = useState(false);

    const getStatsInfo = (key: string) => {
        switch (key) {
            case "new_clauses":
                return {
                    label: "New Clauses",
                    description: "These new clauses will be added",
                    warning: false,
                };
            case "clause_updates":
                return {
                    label: "Clause Updates",
                    description: "These existing clauses will be updated",
                    warning: false,
                };
            case "unchanged_clauses":
                return {
                    label: "Unchanged Clauses",
                    description: "These existing clauses will not be updated",
                    warning: false,
                };
            case "clause_duplicates":
                return {
                    label: "Clause Duplicates",
                    description: `These clauses are duplicates and will be skipped`,
                    warning: false,
                };
            case "skipped_clauses":
                return {
                    label: "Skipped Clauses",
                    value: results.skipped_clauses.length,
                    description: `Clauses that will be skipped due to errors`,
                    warning: false,
                    showDetails: true,
                };
            case "new_examples":
                return {
                    label: "New Clause Examples",
                    description: "These new clause examples will be added",
                    warning: false,
                };
            case "unchanged_examples":
                return {
                    label: "Unchanged Clause Examples",
                    description:
                        "These existing clause examples will not be updated",
                    warning: false,
                };
            case "example_duplicates":
                return {
                    label: "Clause Example Duplicates",
                    description:
                        "These clause examples are duplicates and will be skipped",
                    warning: false,
                };
            case "example_deletions":
                return {
                    label: "Clause Example Deletions",
                    description:
                        "Existing clause examples not found in the import will be deleted",
                    warning: true,
                };
            default:
                return {
                    label: key,
                    description: "",
                    warning: false,
                };
        }
    };

    // Transform stats object to rows for DataGrid
    const rows = Object.entries(results).map(([key, value], index) => ({
        id: index,
        key,
        value,
        ...getStatsInfo(key),
    }));

    const columns: GridColDef[] = [
        {
            field: "label",
            headerName: "Type",
            flex: 1,
            minWidth: 200,
            sortable: false,
        },
        {
            field: "value",
            headerName: "Count",
            minWidth: 120,
            align: "center",
            headerAlign: "center",
            sortable: false,
        },
        {
            field: "description",
            headerName: "",
            flex: 2,
            minWidth: 300,
            renderCell: (params: GridRenderCellParams) => {
                const { warning, description, key, showDetails, value } =
                    params.row;

                if (warning) {
                    return (
                        <Stack direction="row" alignItems="center" gap={1}>
                            <WarningIcon color="warning" fontSize="small" />
                            <Typography
                                fontWeight="bold"
                                color="warning.main"
                                variant="body2"
                            >
                                Warning: Clause examples will be deleted
                            </Typography>
                        </Stack>
                    );
                }

                if (key === "skipped_clauses" && showDetails) {
                    return (
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body2">
                                {description}
                            </Typography>

                            {value > 0 && (
                                <Button
                                    variant="text"
                                    size="small"
                                    type="button"
                                    color="inherit"
                                    startIcon={
                                        showSkippedDetails ? (
                                            <KeyboardArrowUpIcon />
                                        ) : (
                                            <KeyboardArrowDownIcon />
                                        )
                                    }
                                    onClick={() =>
                                        setShowSkippedDetails(
                                            !showSkippedDetails
                                        )
                                    }
                                >
                                    {showSkippedDetails
                                        ? "Hide Details"
                                        : "Show Details"}
                                </Button>
                            )}
                        </Stack>
                    );
                }

                return description;
            },
        },
    ];

    return (
        <>
            <Stack width={"100%"} gap={2}>
                <Stack alignItems={"center"}>
                    <Typography variant="h6" fontWeight={600}>
                        Bulk Import Preview
                    </Typography>

                    <Typography variant={"body2"} color={"textSecondary"}>
                        Verify the results below and click <b>Import</b> to
                        proceed.
                    </Typography>
                </Stack>

                <Table
                    rows={rows}
                    columns={columns}
                    disableColumnMenu
                    hideFooter
                    disableColumnFilter
                    disableColumnSorting
                />
                {showSkippedDetails && (
                    <SkippedClausesList
                        skippedClauses={results.skipped_clauses}
                    />
                )}
            </Stack>
            <Stack
                direction={"row"}
                gap={3}
                maxWidth={isSmall ? 118 : 379}
                width={"100%"}
            >
                <Button
                    type="button"
                    variant={"outlined"}
                    color={"dark"}
                    sx={buttonSx}
                    fullWidth
                    onClick={onClose}
                >
                    Cancel
                </Button>

                <Button
                    variant={"contained"}
                    color={"dark"}
                    disableElevation
                    sx={buttonSx}
                    fullWidth
                >
                    Import
                </Button>
            </Stack>
        </>
    );
};
